'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import CustomDropdown from '@/components/CustomDropdown'
import MultiSelectDropdown from '@/components/MultiSelectDropdown'
import SearchDropdown from '@/components/SearchDropdown'
import { 
  SearchIcon, TrophyIcon, TargetIcon, CalculatorIcon, StarIcon, BookOpenIcon,
  CheckIcon, BoltIcon, FilterIcon, TrendingUpIcon, InfoIcon
} from '@/components/Icons'

interface Problem {
  id: string
  title: string
  slug: string
  difficulty: string
  category: string
  year?: number
  problemNumber?: number
  topics: string[]
  mathLevel: string
  attempts: number
  solved: number
  likes: number
}

export default function ProblemsPage() {
  const router = useRouter()
  const [problems, setProblems] = useState<Problem[]>([])
  const [filteredProblems, setFilteredProblems] = useState<Problem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [difficultyFilter, setDifficultyFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [mathLevelFilter, setMathLevelFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [sortBy, setSortBy] = useState<'difficulty' | 'level' | 'source' | 'newest' | 'popular'>('newest')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchProblems()
  }, [])

  useEffect(() => {
    filterProblems()
  }, [searchTerm, difficultyFilter, categoryFilter, mathLevelFilter, sourceFilter, sortBy, problems])

  const fetchProblems = async () => {
    try {
      const response = await fetch('/api/problems')
      const data = await response.json()
      if (response.ok) {
        setProblems(data)
        setFilteredProblems(data)
      }
    } catch (error) {
      console.error('Error fetching problems:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const filterProblems = () => {
    let filtered = problems

    if (searchTerm) {
      filtered = filtered.filter(problem =>
        problem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        problem.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase())) ||
        problem.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(problem => 
        problem.difficulty.toLowerCase() === difficultyFilter.toLowerCase()
      )
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(problem =>
        problem.category.toLowerCase() === categoryFilter.toLowerCase()
      )
    }

    if (mathLevelFilter !== 'all') {
      filtered = filtered.filter(problem =>
        problem.mathLevel.toLowerCase() === mathLevelFilter.toLowerCase()
      )
    }

    if (sourceFilter !== 'all') {
      filtered = filtered.filter(problem => {
        if (sourceFilter === 'aops') return problem.category === 'AIME' || problem.category === 'AMC'
        if (sourceFilter === 'competition') return ['AIME', 'AMC', 'MathCounts', 'IOI'].includes(problem.category)
        if (sourceFilter === 'practice') return problem.category === 'General'
        return true
      })
    }

    // Sort problems
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'difficulty':
          const diffOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 }
          return (diffOrder[a.difficulty as keyof typeof diffOrder] || 0) - 
                 (diffOrder[b.difficulty as keyof typeof diffOrder] || 0)
        case 'level':
          const levelOrder = { 
            'Pre-Algebra': 1, 'Algebra I': 2, 'Algebra II': 3, 
            'Geometry': 4, 'Pre-Calculus': 5, 'Calculus': 6 
          }
          return (levelOrder[a.mathLevel as keyof typeof levelOrder] || 0) - 
                 (levelOrder[b.mathLevel as keyof typeof levelOrder] || 0)
        case 'source':
          return a.category.localeCompare(b.category)
        case 'popular':
          return b.likes - a.likes
        case 'newest':
        default:
          return b.year || 0 - (a.year || 0)
      }
    })

    setFilteredProblems(sorted)
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return 'difficulty-easy'
      case 'medium':
        return 'difficulty-medium'
      case 'hard':
        return 'difficulty-hard'
      default:
        return 'text-gray-400'
    }
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      'AIME': 'category-aime',
      'AMC': 'category-amc',
      'MathCounts': 'category-mathcounts',
      'IOI': 'category-ioi',
      'General': 'category-general'
    }
    return colors[category as keyof typeof colors] || 'category-general'
  }

  const getSuccessRate = (solved: number, attempts: number) => {
    if (attempts === 0) return 0
    return Math.round((solved / attempts) * 100)
  }

  const allCategories = Array.from(new Set(problems.map(p => p.category)))
  const allMathLevels = Array.from(new Set(problems.map(p => p.mathLevel)))

  const categoryOptions = [
    { value: 'all', label: 'All Categories', description: 'Show problems from all sources', icon: <SearchIcon className="h-4 w-4" /> },
    { value: 'AIME', label: 'AIME', description: 'American Invitational Mathematics Examination', icon: <TrophyIcon className="h-4 w-4" />, color: 'category-aime' },
    { value: 'AMC', label: 'AMC', description: 'American Mathematics Competition', icon: <TargetIcon className="h-4 w-4" />, color: 'category-amc' },
    { value: 'MathCounts', label: 'MathCounts', description: 'National middle school competition', icon: <CalculatorIcon className="h-4 w-4" />, color: 'category-mathcounts' },
    { value: 'IOI', label: 'IOI', description: 'International Math Olympiad style', icon: <StarIcon className="h-4 w-4" />, color: 'category-ioi' },
    { value: 'General', label: 'General', description: 'Practice problems for all levels', icon: <BookOpenIcon className="h-4 w-4" />, color: 'category-general' },
  ]

  const mathLevelOptions = [
    { value: 'all', label: 'All Math Levels', description: 'Show problems from all levels', icon: <BookOpenIcon className="h-4 w-4" /> },
    { value: 'Pre-Algebra', label: 'Pre-Algebra', description: 'Basic algebra and arithmetic', icon: <BookOpenIcon className="h-4 w-4" /> },
    { value: 'Algebra I', label: 'Algebra I', description: 'Linear equations and functions', icon: <BookOpenIcon className="h-4 w-4" /> },
    { value: 'Algebra II', label: 'Algebra II', description: 'Quadratics and polynomials', icon: <BookOpenIcon className="h-4 w-4" /> },
    { value: 'Geometry', label: 'Geometry', description: 'Shapes, angles, and proofs', icon: <BookOpenIcon className="h-4 w-4" /> },
    { value: 'Pre-Calculus', label: 'Pre-Calculus', description: 'Advanced functions and trigonometry', icon: <BookOpenIcon className="h-4 w-4" /> },
    { value: 'Calculus', label: 'Calculus', description: 'Derivatives and integrals', icon: <BookOpenIcon className="h-4 w-4" /> },
  ]

  const difficultyOptions = [
    { value: 'all', label: 'All Difficulties', description: 'Show problems of all difficulty levels', icon: <SearchIcon className="h-4 w-4" /> },
    { value: 'easy', label: 'Easy', description: 'Beginner-friendly problems', icon: <CheckIcon className="h-4 w-4" />, color: 'difficulty-easy' },
    { value: 'medium', label: 'Medium', description: 'Intermediate challenge', icon: <TargetIcon className="h-4 w-4" />, color: 'difficulty-medium' },
    { value: 'hard', label: 'Hard', description: 'Advanced problem solving', icon: <BoltIcon className="h-4 w-4" />, color: 'difficulty-hard' },
  ]

  const sourceOptions = [
    { value: 'all', label: 'All Sources', description: 'Show problems from all sources', icon: <SearchIcon className="h-4 w-4" /> },
    { value: 'aops', label: 'AoPS Problems', description: 'Art of Problem Solving sourced', icon: <StarIcon className="h-4 w-4" /> },
    { value: 'competition', label: 'Competition', description: 'Official competition problems', icon: <TrophyIcon className="h-4 w-4" /> },
    { value: 'practice', label: 'Practice', description: 'General practice problems', icon: <BookOpenIcon className="h-4 w-4" /> },
  ]

  const sortOptions = [
    { value: 'newest', label: 'Newest', description: 'Most recently added problems', icon: <TrendingUpIcon className="h-4 w-4" /> },
    { value: 'difficulty', label: 'Difficulty', description: 'Easy to hard progression', icon: <TargetIcon className="h-4 w-4" /> },
    { value: 'level', label: 'Math Level', description: 'Pre-Algebra to Calculus', icon: <BookOpenIcon className="h-4 w-4" /> },
    { value: 'source', label: 'Source', description: 'Grouped by problem source', icon: <SearchIcon className="h-4 w-4" /> },
    { value: 'popular', label: 'Most Popular', description: 'Highest rated problems', icon: <StarIcon className="h-4 w-4" /> },
  ]

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="bg-dark-700 h-8 w-64 rounded mb-8"></div>
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="bg-dark-700 h-16 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold glow-text mb-4">Math Problems</h1>
        <p className="text-gray-200 mb-6">
          Practice mathematics from competitions like AIME, AMC, MathCounts, and more!
        </p>
        
        <div className="space-y-6">
          <SearchDropdown
            placeholder="Search problems by title, topic, category, or difficulty..."
            onSearch={setSearchTerm}
            className="w-full"
          />
          
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
              <CustomDropdown
                options={categoryOptions}
                value={categoryFilter}
                onChange={setCategoryFilter}
                placeholder="Select Category"
              />

              <CustomDropdown
                options={mathLevelOptions}
                value={mathLevelFilter}
                onChange={setMathLevelFilter}
                placeholder="Select Math Level"
              />
              
              <CustomDropdown
                options={difficultyOptions}
                value={difficultyFilter}
                onChange={setDifficultyFilter}
                placeholder="Select Difficulty"
              />

              <CustomDropdown
                options={sourceOptions}
                value={sourceFilter}
                onChange={setSourceFilter}
                placeholder="Select Source"
              />
            </div>

            {/* Sort dropdown integrated with filters */}
            <div className="flex items-center gap-3 lg:min-w-[250px]">
              <label className="text-sm text-gray-200 font-medium whitespace-nowrap">Sort by:</label>
              <CustomDropdown
                options={sortOptions}
                value={sortBy}
                onChange={(value) => setSortBy(value as any)}
                placeholder="Select sorting"
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* Results summary bar */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 p-4 bg-dark-800/50 rounded-lg border border-dark-700">
          <div className="text-sm text-gray-200 flex items-center gap-2">
            <SearchIcon className="h-4 w-4 text-primary-400" />
            Showing <span className="font-semibold text-white">{filteredProblems.length}</span> of <span className="font-semibold text-white">{problems.length}</span> problems
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            {categoryFilter !== 'all' && (
              <span className="px-2 py-1 bg-dark-700 rounded-md">
                Category: <span className="text-white">{categoryFilter}</span>
              </span>
            )}
            {mathLevelFilter !== 'all' && (
              <span className="px-2 py-1 bg-dark-700 rounded-md">
                Level: <span className="text-white">{mathLevelFilter}</span>
              </span>
            )}
            {difficultyFilter !== 'all' && (
              <span className="px-2 py-1 bg-dark-700 rounded-md">
                Difficulty: <span className="text-white">{difficultyFilter}</span>
              </span>
            )}
            {sourceFilter !== 'all' && (
              <span className="px-2 py-1 bg-dark-700 rounded-md">
                Source: <span className="text-white">{sourceFilter}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {filteredProblems.length === 0 ? (
        <div className="text-center py-12">
          <CalculatorIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-200 text-lg">No problems found</p>
          <p className="text-gray-300">Try adjusting your search filters</p>
        </div>
      ) : (
        <div className="card-energy overflow-hidden">
          <div className="overflow-x-auto">
            <div className="mb-3 px-4 py-2 bg-blue-900/20 border border-blue-700/30 rounded text-sm text-blue-300 flex items-center gap-2">
              <InfoIcon className="h-4 w-4" />
              Tip: Click on the <span className="text-primary-400 font-medium">blue problem titles</span> to view the problem details and start solving!
            </div>
            <table className="w-full bg-dark-800 overflow-hidden rounded-lg">
              <thead className="bg-dark-700 border-b border-dark-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200">
                    Problem
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200">
                    Math Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-200">
                    Topics
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredProblems.map((problem) => (
                  <tr key={problem.id} className="border-b border-dark-700 hover:bg-dark-700/30 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-white">
                      <span className="h-5 w-5 text-gray-600">○</span>
                    </td>
                    <td className="px-6 py-4 text-white">
                      <div>
                        <Link 
                          href={`/problems/${problem.slug}`}
                          className="text-primary-400 hover:text-primary-300 font-semibold cursor-pointer hover:underline transition-all duration-200 block py-2 px-3 -mx-3 rounded-md hover:bg-primary-900/30 relative z-20 border border-transparent hover:border-primary-500/30"
                          style={{ 
                            pointerEvents: 'auto',
                            position: 'relative',
                            zIndex: 20,
                            display: 'block'
                          }}
                        >
                          {problem.title}
                        </Link>
                        <div className="flex items-center mt-1">
                          <span className={`text-sm font-medium ${getDifficultyColor(problem.difficulty)}`}>
                            {problem.difficulty}
                          </span>
                          {problem.year && (
                            <span className="ml-2 text-xs text-gray-300">
                              {problem.year}
                              {problem.problemNumber && ` #${problem.problemNumber}`}
                            </span>
                          )}
                        </div>
                        {problem.source && (
                          <div className="text-xs text-gray-400 mt-1">
                            Source: {problem.source}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-white">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(problem.category)}`}>
                        {problem.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      <span className="math-level px-2 py-1 rounded-full text-xs font-medium">
                        {problem.mathLevel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {getSuccessRate(problem.solved, problem.attempts)}%
                    </td>
                    <td className="px-6 py-4 text-white">
                      <div className="flex flex-wrap gap-1">
                        {problem.topics.slice(0, 3).map(topic => (
                          <span
                            key={topic}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-dark-700 text-white border border-dark-600"
                          >
                            {topic}
                          </span>
                        ))}
                        {problem.topics.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-dark-700 text-white border border-dark-600">
                            +{problem.topics.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Statistics */}
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
        {allCategories.map(category => {
          const categoryProblems = problems.filter(p => p.category === category)
          return (
            <div key={category} className="card-energy p-4 text-center">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-2 ${getCategoryColor(category)}`}>
                {category}
              </div>
              <div className="text-2xl font-bold text-white">
                {categoryProblems.length}
              </div>
              <div className="text-sm text-gray-200">Problems</div>
            </div>
          )
        })}
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <div className="card-energy p-6 text-center">
          <CalculatorIcon className="h-12 w-12 text-blue-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Competition Problems</h3>
          <p className="text-gray-200 text-sm">
            Solve problems from AIME, AMC, MathCounts, and other prestigious competitions.
          </p>
        </div>
        
        <div className="card-energy p-6 text-center">
          <TrophyIcon className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">All Math Levels</h3>
          <p className="text-gray-200 text-sm">
            From pre-algebra to calculus, find problems that match your skill level.
          </p>
        </div>
        
        <div className="card-energy p-6 text-center">
          <CheckIcon className="h-12 w-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2 glow-text-secondary">Solution Sharing</h3>
          <p className="text-gray-200 text-sm">
            Share your solutions and learn from others after solving problems.
          </p>
        </div>
      </div>
    </div>
  )
}